import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DollarSign, RefreshCw, Loader2, CheckCircle2, Globe } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_USD = `${BACKEND_URL}/api/carteira-usd`;
const API_USA = `${BACKEND_URL}/api/usa`;

const formatarUSD = (valor) => `$${(valor || 0).toFixed(2)}`;

const RepassesUSD = () => {
  const [carregando, setCarregando] = useState(true);
  const [grupos, setGrupos] = useState([]);
  const [processandoId, setProcessandoId] = useState(null);
  const [modalRepasse, setModalRepasse] = useState(null);

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const resp = await authService.fetchAuth(`${API_USD}/admin/pedidos-aguardando-repasse`);
      if (resp.ok) setGrupos(await resp.json());
    } catch (error) {
      console.error("Erro ao carregar repasses USD:", error);
      toast.error("Não foi possível carregar os repasses internacionais.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const confirmarEntrega = async (pedidoId) => {
    setProcessandoId(pedidoId);
    try {
      const resp = await authService.fetchAuth(`${API_USA}/pedidos/${pedidoId}/confirmar-entrega`, {
        method: "POST",
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao confirmar entrega");
      toast.success("Entrega confirmada.");
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessandoId(null);
    }
  };

  const confirmarAutorizacaoRepasse = async () => {
    if (!modalRepasse) return;
    const grupo = modalRepasse;
    setProcessandoId(grupo.fornecedor_id);
    try {
      const resp = await authService.fetchAuth(`${API_USD}/admin/repasse/autorizar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pedidos_ids: grupo.pedidos.map((p) => p.id) }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao autorizar repasse");
      toast.success(`Repasse de ${formatarUSD(grupo.valor_total_usd)} autorizado.`);
      setModalRepasse(null);
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessandoId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Globe className="w-7 h-7 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Repasses Internacionais (USD)</h1>
            <p className="text-sm text-gray-400">Confirme entregas e autorize repasses aos fornecedores internacionais</p>
          </div>
        </div>
        <Button variant="outline" onClick={carregarDados}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : grupos.length === 0 ? (
        <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
          Nenhum pedido internacional aguardando repasse no momento.
        </Card>
      ) : (
        <div className="space-y-4">
          {grupos.map((grupo) => (
            <Card key={grupo.fornecedor_id} className="p-4 bg-gray-800/50 border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-medium">Fornecedor: {grupo.fornecedor_id}</p>
                <p className="text-green-400 font-bold flex items-center">
                  <DollarSign className="w-4 h-4" />{grupo.valor_total_usd.toFixed(2)}
                </p>
              </div>
              <div className="space-y-2 mb-3">
                {grupo.pedidos.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm bg-gray-900/50 p-2 rounded">
                    <span className="text-gray-300">{p.codigo} — {p.produto_nome}</span>
                    {p.status !== "entregue" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => confirmarEntrega(p.id)}
                        disabled={processandoId === p.id}
                      >
                        Confirmar entrega
                      </Button>
                    ) : (
                      <span className="text-green-400 text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Entregue
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 w-full"
                onClick={() => setModalRepasse(grupo)}
                disabled={grupo.pedidos.some((p) => p.status !== "entregue")}
              >
                Autorizar repasse
              </Button>
            </Card>
          ))}
        </div>
      )}

      {modalRepasse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="p-6 max-w-md w-full bg-gray-900 border-gray-700">
            <h3 className="font-semibold text-white mb-2">Autorizar repasse internacional</h3>
            <p className="text-2xl font-bold text-emerald-400 mb-4">{formatarUSD(modalRepasse.valor_total_usd)}</p>
            <p className="text-sm text-gray-400 mb-4">
              Isso libera a garantia reservada em dólar e credita o fornecedor internacional. Essa ação não pode ser desfeita por aqui.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalRepasse(null)}>Cancelar</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={confirmarAutorizacaoRepasse}
                disabled={processandoId === modalRepasse.fornecedor_id}
              >
                {processandoId === modalRepasse.fornecedor_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sim, autorizar repasse
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RepassesUSD;
